/**
 * Polls one consent request only while the answer can still change, then stops. Refetching on
 * focus covers the common case: the advisor talks the customer through it and comes back.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { agentEnteredIntakeApi, agentEnteredIntakeKeys } from '@/features/invite-client';

const POLL_INTERVAL_MS = 10_000;

interface UseAgentEnteredIntakeStatusOptions {
  onCancelled?: () => void;
}

export function useAgentEnteredIntakeStatus(
  requestId: string | null,
  { onCancelled }: UseAgentEnteredIntakeStatusOptions = {},
) {
  const queryClient = useQueryClient();
  const queryKey = agentEnteredIntakeKeys.detail(requestId ?? '');

  const query = useQuery({
    queryKey,
    queryFn: () => agentEnteredIntakeApi.status(requestId!),
    enabled: !!requestId,
    refetchOnWindowFocus: true,
    refetchInterval: ({ state }) => {
      const status = state.data?.status;
      const isSettled = status === 'consented' || status === 'cancelled' || state.data?.isExpired;
      return isSettled ? false : POLL_INTERVAL_MS;
    },
  });

  const resend = useMutation({
    mutationFn: () => agentEnteredIntakeApi.resend(requestId!),
    // A refused send answers 200 with a status rather than throwing, so the outcome has to be
    // read here. Reporting it as sent is the whole bug this flow had.
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey });

      if (data.status === 'undeliverable') {
        toast.error('We couldn’t email that address', {
          description: 'It has unsubscribed from Waterlily emails. Try another address.',
        });
        return;
      }

      if (data.status === 'send-failed') {
        toast.error('We couldn’t send that email', {
          description: 'Nothing reached your client. This didn’t use up an attempt — try again.',
        });
        return;
      }

      // The previous link is dead now, so say so rather than letting the advisor tell the
      // customer to use the older email.
      toast.success('Permission request sent again', {
        description: 'The previous link no longer works.',
      });
    },
    onError: (error: unknown) => {
      toast.error(
        error instanceof Error ? error.message : 'Could not send that again. Please try later.',
      );
    },
  });

  const cancel = useMutation({
    mutationFn: () => agentEnteredIntakeApi.cancel(requestId!),
    onSuccess: () => {
      toast.success('Request cancelled');
      queryClient.invalidateQueries({ queryKey });
      onCancelled?.();
    },
    onError: () => toast.error('Could not cancel that request.'),
  });

  return {
    request: query.data ?? null,
    isLoading: query.isPending && !!requestId,
    isConsented: query.data?.status === 'consented',
    resend: resend.mutate,
    isResending: resend.isPending,
    cancel: cancel.mutate,
    isCancelling: cancel.isPending,
  };
}
